var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useState } from "react";
import { jwtDecode } from "jwt-decode";
import LocalStorageService from "../infrastructure/local-storage-service";
import useAsyncEffect from "use-async-effect";
import UsersApi from "../infrastructure/api/pages/users-api";
import i18n from "../i18n";
const context = {
    setAccessToken: (token) => { },
    getProfile: () => {
        return {};
    },
    setQRCodeIsConfigured: (_isConfigured) => { },
    isConfigured: false,
    setLanguage: (language) => { },
    selectedLanguage: "",
    // Current user's Initials (Item #1 timestamp feature). Fetched once per
    // session alongside the existing locale fetch below — never re-fetched per
    // timestamp insert. setInitials lets the self-edit flow update it in place
    // without requiring a re-login.
    initials: "",
    setInitials: (_initials) => { },
    // Name and email as stored in the database. getProfile() reads them from the
    // JWT, which keeps the claims it was issued with — so a self-edit is invisible
    // there until a new token is issued. This is the live copy.
    account: {},
    // Re-pulls the record above. Call it after the current user edits their own
    // profile, the same way UserRolesPermissionsSet() re-pulls permissions.
    refreshAccount: () => { },
};
export const AuthenticationContext = createContext(context);
export const AuthenticationContextProvider = ({ children }) => {
    const [token, setToken] = useState("");
    const [isConfigured, setIsConfigured] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState("");
    const [initials, setInitials] = useState("");
    const [account, setAccount] = useState({});
    const setAccessToken = (token) => {
        LocalStorageService.setAccessToken(token);
        setToken(token);
    };
    const setQRCodeIsConfigured = (_isConfigured) => {
        setIsConfigured(_isConfigured);
    };
    const loadAccount = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const userId = getProfile().userId;
        if (!userId)
            return;
        const res = yield UsersApi.getAdminUserById(userId);
        if (res.data.locale) {
            setSelectedLanguage(res.data.locale);
            i18n.changeLanguage(res.data.locale);
        }
        setInitials((_a = res.data.initials) !== null && _a !== void 0 ? _a : "");
        setAccount({
            name: [res.data.firstName, res.data.lastName].filter(Boolean).join(" "),
            email: res.data.email,
        });
    });
    useAsyncEffect(loadAccount, []);
    const refreshAccount = () => {
        loadAccount().catch(() => {
            /* display-only refresh: the save already succeeded, so a failure here
               just leaves the previous values on screen until the next load */
        });
    };
    const getProfile = () => {
        const _token = token || LocalStorageService.getAccessToken();
        if (_token) {
            const tempProfile = jwtDecode(_token);
            const keys = Object.keys(tempProfile);
            let profileKeys = [];
            keys.forEach((k) => {
                const key = k.split("/")[k.split("/").length - 1];
                const value = tempProfile[k];
                profileKeys.push({ key, value });
            });
            const _profile = {
                emailaddress: profileKeys.find((f) => f.key === "emailaddress")
                    .value,
                name: profileKeys.find((f) => f.key === "name").value,
                role: profileKeys.find((f) => f.key === "role").value,
                userId: profileKeys.find((f) => f.key === "nameidentifier").value,
                locale: profileKeys.find((f) => f.key === "locality").value,
            };
            return _profile;
        }
        else {
            const profile = {
                emailaddress: "",
                name: "",
                role: "",
                locale: "nl-NL",
                userId: "",
            };
            return profile;
        }
    };
    const setLanguage = (language) => {
        i18n.changeLanguage(language);
        const request = {
            id: getProfile().userId,
            roles: [],
            locale: language,
        };
        if (selectedLanguage !== request.locale) {
            setSelectedLanguage(language);
            UsersApi.updateUserLocale(request);
        }
    };
    const contextValue = {
        setAccessToken,
        getProfile,
        setQRCodeIsConfigured,
        isConfigured,
        setLanguage,
        selectedLanguage,
        initials,
        setInitials,
        account,
        refreshAccount,
    };
    return (_jsx(AuthenticationContext.Provider, { value: contextValue, children: children }));
};
